import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  Plus,
  Pencil,
  Trash2,
  GraduationCap,
  X,
  ExternalLink,
  ClipboardList,
  Search,
} from "lucide-react";
import { useState, useMemo } from "react";

import { EmptyState } from "~/components/ui/empty-state";
import { RichTextEditor } from "~/components/ui/rich-text-editor";
import { cn } from "~/lib/cn";
import { requirePermission } from "~/lib/functions/admin-access";
import {
  adminCtpsQueryOptions,
  adminCtpQueryKeys,
  ccoDropdownQueryOptions,
  createCtpFn,
  updateCtpFn,
  deleteCtpFn,
  type AdminCtpRow,
} from "~/lib/functions/admin-ctps";
import { generateSlug } from "~/lib/shared/slug";

export const Route = createFileRoute("/admin/ctps/")({
  beforeLoad: ({ context }) =>
    requirePermission(context.adminPermissions, "cih:read"),
  loader: async ({ context }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(adminCtpsQueryOptions),
      context.queryClient.ensureQueryData(ccoDropdownQueryOptions),
    ]);
    return {};
  },
  component: CtpsAdminPage,
});

const INPUT_CLASS =
  "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100";

interface CtpFormState {
  name: string;
  slug: string;
  ccoId: string;
  website: string;
  logoUrl: string;
  description: string;
  deliveryFormat: string;
  accreditationLevel: string;
  duration: string;
  tuitionRange: string;
  body: string;
  prerequisites: string;
  icfHours: string;
}

const EMPTY_FORM: CtpFormState = {
  name: "",
  slug: "",
  ccoId: "",
  website: "",
  logoUrl: "",
  description: "",
  deliveryFormat: "",
  accreditationLevel: "",
  duration: "",
  tuitionRange: "",
  body: "",
  prerequisites: "",
  icfHours: "",
};

function formFromRow(row: AdminCtpRow): CtpFormState {
  return {
    name: row.name,
    slug: row.slug ?? "",
    ccoId: row.ccoId,
    website: row.website ?? "",
    logoUrl: row.logoUrl ?? "",
    description: row.description ?? "",
    deliveryFormat: row.deliveryFormat ?? "",
    accreditationLevel: row.accreditationLevel ?? "",
    duration: row.duration ?? "",
    tuitionRange: row.tuitionRange ?? "",
    body: row.body ? JSON.stringify(row.body) : "",
    prerequisites: row.prerequisites ?? "",
    icfHours: row.icfHours ?? "",
  };
}

function CtpsAdminPage() {
  const queryClient = useQueryClient();

  const { data: ctps, isLoading } = useQuery(adminCtpsQueryOptions);
  const { data: ccoOptions } = useQuery(ccoDropdownQueryOptions);

  const [editingCtp, setEditingCtp] = useState<AdminCtpRow | null>(null);
  const [form, setForm] = useState<CtpFormState>(EMPTY_FORM);
  const [search, setSearch] = useState("");
  const [ccoFilter, setCcoFilter] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingCtp(null);
  };

  const startEdit = (row: AdminCtpRow) => {
    setEditingCtp(row);
    setForm(formFromRow(row));
  };

  const setField = <K extends keyof CtpFormState>(
    key: K,
    value: CtpFormState[K],
  ) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "name" && !editingCtp) {
        next.slug = generateSlug(value);
      }
      return next;
    });
  };

  // Mutations
  const createMutation = useMutation({
    mutationFn: createCtpFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCtpQueryKeys.all });
      resetForm();
      showSuccess("CTP created successfully.");
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateCtpFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCtpQueryKeys.all });
      resetForm();
      showSuccess("CTP updated successfully.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCtpFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminCtpQueryKeys.all });
      showSuccess("CTP deleted successfully.");
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim() || !form.ccoId) return;

    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      ccoId: form.ccoId,
      website: form.website.trim() || null,
      logoUrl: form.logoUrl.trim() || null,
      description: form.description.trim() || null,
      deliveryFormat: form.deliveryFormat.trim() || undefined,
      accreditationLevel: form.accreditationLevel.trim() || undefined,
      duration: form.duration.trim() || undefined,
      tuitionRange: form.tuitionRange.trim() || undefined,
      body: form.body.trim() || undefined,
      prerequisites: form.prerequisites.trim() || undefined,
      icfHours: form.icfHours.trim() || undefined,
    };

    if (editingCtp) {
      updateMutation.mutate({ data: { id: editingCtp.id, ...payload } });
    } else {
      createMutation.mutate({ data: payload });
    }
  };

  const handleDelete = (row: AdminCtpRow) => {
    if (!window.confirm(`Delete "${row.name}"? This cannot be undone.`)) return;
    deleteMutation.mutate({ data: { id: row.id } });
  };

  // Filtering
  const filteredCtps = useMemo(() => {
    if (!ctps) return [];
    const lowerSearch = search.toLowerCase();
    return ctps.filter((ctp) => {
      if (
        lowerSearch &&
        !ctp.name.toLowerCase().includes(lowerSearch) &&
        !(ctp.slug ?? "").toLowerCase().includes(lowerSearch)
      )
        return false;
      if (ccoFilter && ctp.ccoId !== ccoFilter) return false;
      return true;
    });
  }, [ctps, search, ccoFilter]);

  // Summary stats
  const totalCtps = ctps?.length ?? 0;
  const withCoaches = ctps?.filter((c) => c.coachCount > 0).length ?? 0;

  const isPending =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending;
  const mutationError =
    createMutation.error || updateMutation.error || deleteMutation.error;

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-5xl">
        <h1 className="mb-2 text-2xl font-bold text-gray-900">
          CTP Management
        </h1>
        <p className="mb-6 text-sm text-gray-500">
          Manage Coach Training Programs and their CCO associations.
        </p>

        {/* Success Banner */}
        {successMessage && (
          <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {successMessage}
          </div>
        )}

        {/* Error Banner */}
        {mutationError && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            An error occurred. Please try again.
          </div>
        )}

        {/* Summary Cards */}
        <div className="mb-6 grid grid-cols-2 gap-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="text-2xl font-bold text-gray-900">{totalCtps}</div>
            <div className="text-xs text-gray-500">
              Total CTP{totalCtps !== 1 ? "s" : ""}
            </div>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-emerald-500" />
              <div className="text-2xl font-bold text-gray-900">
                {withCoaches}
              </div>
            </div>
            <div className="text-xs text-gray-500">With Certified Coaches</div>
          </div>
        </div>

        {/* Search + CCO Filter */}
        <div className="mb-6 flex items-center gap-3">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or slug..."
            className={cn(INPUT_CLASS, "max-w-xs")}
          />
          <select
            value={ccoFilter}
            onChange={(e) => setCcoFilter(e.target.value)}
            className={cn(INPUT_CLASS, "max-w-xs")}
          >
            <option value="">All CCOs</option>
            {ccoOptions?.map((cco) => (
              <option key={cco.id} value={cco.id}>
                {cco.name}
              </option>
            ))}
          </select>
        </div>

        {/* Create / Edit Form */}
        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold tracking-wide text-gray-500 uppercase">
              {editingCtp ? `Edit: ${editingCtp.name}` : "Create New CTP"}
            </h2>
            {editingCtp && (
              <button
                type="button"
                onClick={resetForm}
                className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="e.g., ICF Level 2 Certification"
                  className={INPUT_CLASS}
                  required
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Slug <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setField("slug", e.target.value)}
                  placeholder="auto-generated-from-name"
                  className={INPUT_CLASS}
                  required
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                CCO <span className="text-red-400">*</span>
              </label>
              <select
                value={form.ccoId}
                onChange={(e) => setField("ccoId", e.target.value)}
                className={INPUT_CLASS}
                required
              >
                <option value="">Select a CCO...</option>
                {ccoOptions?.map((cco) => (
                  <option key={cco.id} value={cco.id}>
                    {cco.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Website{" "}
                  <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  type="url"
                  value={form.website}
                  onChange={(e) => setField("website", e.target.value)}
                  placeholder="https://..."
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Logo URL{" "}
                  <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  type="url"
                  value={form.logoUrl}
                  onChange={(e) => setField("logoUrl", e.target.value)}
                  placeholder="https://..."
                  className={INPUT_CLASS}
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Description{" "}
                <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                rows={3}
                placeholder="Brief description of this training program..."
                className={INPUT_CLASS}
              />
            </div>

            {/* Program Details */}
            <div className="space-y-4 border-t border-gray-100 pt-4">
              <h3 className="text-xs font-semibold tracking-wide text-gray-400 uppercase">
                Program Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Delivery Format{" "}
                    <span className="font-normal text-gray-400">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={form.deliveryFormat}
                    onChange={(e) => setField("deliveryFormat", e.target.value)}
                    placeholder="e.g., Online, In-Person, Hybrid"
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Accreditation Level{" "}
                    <span className="font-normal text-gray-400">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={form.accreditationLevel}
                    onChange={(e) =>
                      setField("accreditationLevel", e.target.value)
                    }
                    placeholder="e.g., ICF Level 1, ICF Level 2"
                    className={INPUT_CLASS}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Duration{" "}
                    <span className="font-normal text-gray-400">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={form.duration}
                    onChange={(e) => setField("duration", e.target.value)}
                    placeholder="e.g., 6 months, 125 hours"
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Tuition Range{" "}
                    <span className="font-normal text-gray-400">
                      (optional)
                    </span>
                  </label>
                  <input
                    type="text"
                    value={form.tuitionRange}
                    onChange={(e) => setField("tuitionRange", e.target.value)}
                    placeholder="e.g., $3,000 - $5,000"
                    className={INPUT_CLASS}
                  />
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  ICF Hours{" "}
                  <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={form.icfHours}
                  onChange={(e) => setField("icfHours", e.target.value)}
                  placeholder="e.g., 60 ACSTH, 125 ACTP"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Prerequisites{" "}
                  <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <textarea
                  value={form.prerequisites}
                  onChange={(e) => setField("prerequisites", e.target.value)}
                  rows={2}
                  placeholder="Any prerequisites for enrollment..."
                  className={INPUT_CLASS}
                />
              </div>
            </div>

            {/* Body Content */}
            <div className="space-y-4 border-t border-gray-100 pt-4">
              <h3 className="text-xs font-semibold tracking-wide text-gray-400 uppercase">
                Body Content
              </h3>
              <RichTextEditor
                content={form.body}
                onChange={(val) => setField("body", val)}
                className="min-h-[200px]"
              />
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={
                  !form.name.trim() ||
                  !form.slug.trim() ||
                  !form.ccoId ||
                  isPending
                }
                className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {editingCtp ? (
                  isPending ? (
                    "Saving..."
                  ) : (
                    "Save Changes"
                  )
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    {isPending ? "Creating..." : "Create CTP"}
                  </>
                )}
              </button>
              {editingCtp && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="rounded-lg px-4 py-2 text-sm text-gray-600 transition hover:bg-gray-100"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* CTPs Table */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          <h2 className="border-b px-6 py-4 text-sm font-semibold tracking-wide text-gray-500 uppercase">
            All CTPs ({filteredCtps.length})
          </h2>
          {!filteredCtps.length ? (
            ctps?.length ? (
              <EmptyState
                size="sm"
                icon={Search}
                title="No matches"
                description="Try adjusting your filters."
              />
            ) : (
              <EmptyState
                size="sm"
                icon={ClipboardList}
                title="No CTPs yet"
                description="Create a training program using the form above."
              />
            )
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50 text-left text-xs tracking-wider text-gray-500 uppercase">
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Parent CCO</th>
                    <th className="px-4 py-3">Coaches</th>
                    <th className="px-4 py-3">Website</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCtps.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b last:border-0 hover:bg-gray-50"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {row.logoUrl ? (
                            <img
                              src={row.logoUrl}
                              alt=""
                              className="h-8 w-8 shrink-0 rounded object-contain"
                            />
                          ) : (
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-gray-100 text-gray-400">
                              <GraduationCap className="h-4 w-4" />
                            </div>
                          )}
                          <div>
                            {row.isPublished ? (
                              <a
                                href={`/training-programs/${row.slug}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-gray-900 hover:text-brand-600 hover:underline"
                              >
                                {row.name}
                                <ExternalLink className="ml-1 inline h-3 w-3 text-gray-400" />
                              </a>
                            ) : (
                              <span className="font-medium text-gray-900">
                                {row.name}
                              </span>
                            )}
                            <div className="text-xs text-gray-400">
                              {row.slug}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{row.ccoName}</td>
                      <td className="px-4 py-3 text-gray-700">
                        {row.coachCount}
                      </td>
                      <td className="px-4 py-3">
                        {row.website ? (
                          <a
                            href={row.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-600 hover:underline"
                          >
                            {row.website
                              .replace(/^https?:\/\//, "")
                              .slice(0, 30)}
                            {row.website.replace(/^https?:\/\//, "").length > 30
                              ? "..."
                              : ""}
                          </a>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            title="Edit CTP"
                            onClick={() => startEdit(row)}
                            className="rounded p-1.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-700"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            title="Delete CTP"
                            onClick={() => handleDelete(row)}
                            disabled={deleteMutation.isPending}
                            className="rounded p-1.5 text-red-500 transition hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
